import React from "react";
import { useSkin } from "../../skins/SkinContext";
import { SkRect, SkEllipse, SkCircle, SkLine, SkPolygon } from "../../prim";
import { skButterfly, skWorm, skFish } from "../../actors3/animals";

// ─── shared props interface ───────────────────────────────────────────────────
export interface SkinSceneProps {
  opacity?: number;
  accentColor?: string;
  label?: string;
  t?: number;
}

// ─── helpers ──────────────────────────────────────────────────────────────────
/** Clamp value to [0, 1]. */
function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

/**
 * Stage opacity: fades in at `start`, fully visible in the middle, fades out
 * before `end`. Pass `noFadeIn=true` for stage 0 which should be visible at t=0.
 */
function stageOpacity(v: number, start: number, end: number, noFadeIn = false): number {
  const ramp = 0.18;
  if (v < start) return 0;
  if (v > end) return 0;
  const inFade = noFadeIn ? 1 : clamp01((v - start) / ramp);
  const outFade = clamp01((end - v) / ramp);
  return Math.min(inFade, outFade);
}

// ─── stage label ─────────────────────────────────────────────────────────────
function StageLabel({
  text,
  opacity,
  textInk,
  fontFamily,
}: {
  text: string;
  opacity: number;
  textInk: string;
  fontFamily: string;
}): React.ReactElement {
  return (
    <g transform="translate(400 390)" opacity={opacity}>
      <rect x={-170} y={-20} width={340} height={36} rx={18} fill="rgba(255,255,255,0.82)" stroke="#cbd5e1" strokeWidth={1.5} />
      <text
        x={0}
        y={5}
        textAnchor="middle"
        fill={textInk}
        fontSize={14}
        fontWeight={700}
        fontFamily={fontFamily}
      >
        {text}
      </text>
    </g>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// SCENE 1: SkinButterflyLifeScene
// Life cycle: egg on leaf → caterpillar → chrysalis → butterfly (~10 s loop)
// ═══════════════════════════════════════════════════════════════════════════════

/** A simple leaf shape built from SkEllipse + SkLine. */
function Leaf({
  x,
  y,
  scale = 1,
}: {
  x: number;
  y: number;
  scale?: number;
}): React.ReactElement {
  return (
    <g transform={`translate(${x} ${y}) scale(${scale})`}>
      <SkEllipse cx={0} cy={0} rx={28} ry={14} fill="#4ade80" seed={2100} />
      <SkLine x1={-26} y1={0} x2={26} y2={0} seed={2101} stroke="#16a34a" />
      <SkLine x1={0} y1={-14} x2={0} y2={14} seed={2102} stroke="#16a34a" />
    </g>
  );
}

/** Egg: a small white-ish oval. */
function Egg({ x, y }: { x: number; y: number }): React.ReactElement {
  return (
    <g transform={`translate(${x} ${y})`}>
      <SkEllipse cx={0} cy={0} rx={9} ry={12} fill="#fef9c3" seed={2110} />
      <SkEllipse cx={0} cy={-4} rx={5} ry={4} fill="#fefce8" seed={2111} />
    </g>
  );
}

/** Chrysalis: a teardrop shape hanging from a line. */
function Chrysalis({
  x,
  y,
  wobble,
}: {
  x: number;
  y: number;
  wobble: number;
}): React.ReactElement {
  return (
    <g transform={`translate(${x} ${y}) rotate(${wobble})`}>
      <SkLine x1={0} y1={-30} x2={0} y2={-54} seed={2120} stroke="#6b7280" strokeWidth={2} />
      <SkEllipse cx={0} cy={0} rx={14} ry={28} fill="#a3e635" seed={2121} />
      <SkEllipse cx={0} cy={-14} rx={10} ry={10} fill="#84cc16" seed={2122} />
      {/* ridge lines */}
      <SkLine x1={-12} y1={-4} x2={12} y2={-4} seed={2123} stroke="#4d7c0f" />
      <SkLine x1={-13} y1={4} x2={13} y2={4} seed={2124} stroke="#4d7c0f" />
      <SkLine x1={-10} y1={12} x2={10} y2={12} seed={2125} stroke="#4d7c0f" />
    </g>
  );
}

export const SkinButterflyLifeScene: React.FC<SkinSceneProps> = ({
  opacity = 1,
  t = 0,
  label = "From egg to butterfly!",
}) => {
  const skin = useSkin();
  const LOOP = 10;
  const v = (t % LOOP) / LOOP; // 0..1 per loop

  // stage boundaries (fraction of LOOP)
  const S0_START = 0;
  const S0_END = 0.28;   // 0–2.8 s : egg on leaf
  const S1_START = 0.22;
  const S1_END = 0.52;   // 2.2–5.2 s : caterpillar
  const S2_START = 0.46;
  const S2_END = 0.76;   // 4.6–7.6 s : chrysalis
  const S3_START = 0.70;
  const S3_END = 1.00;   // 7.0–10 s : butterfly flying off

  const oEgg = stageOpacity(v, S0_START, S0_END, true);
  const oCat = stageOpacity(v, S1_START, S1_END);
  const oChrys = stageOpacity(v, S2_START, S2_END);
  const oBfly = stageOpacity(v, S3_START, S3_END);

  // caterpillar: crawls from left to right across leaf
  const catX = 260 + ((v - S1_START) / (S1_END - S1_START)) * 120;
  const catY = 298;

  // butterfly: emerges near chrysalis, then flies upward & right
  const bflyProgress = clamp01((v - S3_START) / (S3_END - S3_START));
  const bflyX = 450 + bflyProgress * 200;
  const bflyY = 240 - bflyProgress * 130;

  // chrysalis wobble while it's forming
  const chrysWobble = oChrys > 0.1 ? Math.sin(t * 2.5) * 4 : 0;

  const ground = 320;

  // stage label texts
  const stageName =
    oEgg > 0.2
      ? "Stage 1: The egg"
      : oCat > 0.2
      ? "Stage 2: Caterpillar!"
      : oChrys > 0.2
      ? "Stage 3: Chrysalis"
      : "Stage 4: Butterfly!";
  const stageOp = Math.max(oEgg, oCat, oChrys, oBfly);

  return (
    <g opacity={opacity}>
      {/* sky */}
      <rect width={800} height={450} fill={skin.surface} />
      {/* ground */}
      <SkRect x={0} y={ground} w={800} h={450 - ground} fill="#bbf7d0" seed={2000} />

      {/* gentle sky gradient: a soft blue band */}
      <SkRect x={0} y={0} w={800} h={ground} fill="#e0f2fe" seed={2001} opacity={0.45} />

      {/* branch */}
      <SkLine x1={200} y1={260} x2={560} y2={268} seed={2010} stroke="#92400e" strokeWidth={6} />
      <SkLine x1={450} y1={268} x2={450} y2={224} seed={2011} stroke="#92400e" strokeWidth={4} />

      {/* leaf platform the egg/caterpillar sit on */}
      <g transform="translate(330 280)">
        <Leaf x={0} y={0} scale={2.4} />
      </g>

      {/* ── Stage 0: Egg ────────────────────────────────────────────── */}
      <g opacity={oEgg}>
        <Egg x={330} y={264} />
      </g>

      {/* ── Stage 1: Caterpillar (skWorm) ──────────────────────────── */}
      <g opacity={oCat}>
        {skWorm(catX, catY, t, 1.1)}
      </g>

      {/* ── Stage 2: Chrysalis hanging from branch ─────────────────── */}
      <g opacity={oChrys}>
        <Chrysalis x={450} y={238} wobble={chrysWobble} />
      </g>

      {/* ── Stage 3: Butterfly emerging and flying ─────────────────── */}
      <g opacity={oBfly}>
        {skButterfly(bflyX, bflyY, t, 1.3, "#f59e0b")}
      </g>

      {/* main caption */}
      <g transform="translate(400 38)">
        <rect x={-200} y={-22} width={400} height={40} rx={20} fill="rgba(255,255,255,0.88)" stroke="#cbd5e1" strokeWidth={1.5} />
        <text
          x={0}
          y={4}
          textAnchor="middle"
          fill={skin.textInk}
          fontSize={16}
          fontWeight={700}
          fontFamily={skin.fontFamily}
        >
          {label}
        </text>
      </g>

      {/* stage label */}
      <StageLabel
        text={stageName}
        opacity={stageOp}
        textInk={skin.textInk}
        fontFamily={skin.fontFamily}
      />
    </g>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// SCENE 2: SkinFrogLifeScene
// Pond life cycle: frogspawn → tadpole → tadpole-with-legs → frog hopping (~9 s loop)
// ═══════════════════════════════════════════════════════════════════════════════

/** Lily pad: a flat green ellipse with a notch cut in. */
function LilyPad({
  x,
  y,
  scale = 1,
}: {
  x: number;
  y: number;
  scale?: number;
}): React.ReactElement {
  return (
    <g transform={`translate(${x} ${y}) scale(${scale})`}>
      <SkEllipse cx={0} cy={0} rx={42} ry={22} fill="#4ade80" seed={2300} />
      <SkEllipse cx={0} cy={0} rx={36} ry={18} fill="#22c55e" seed={2301} />
      {/* notch */}
      <SkPolygon
        points={[
          [0, 0],
          [-8, -22],
          [8, -22],
        ]}
        fill="#166534"
        seed={2302}
      />
      {/* vein lines */}
      <SkLine x1={0} y1={0} x2={0} y2={22} seed={2303} stroke="#16a34a" />
      <SkLine x1={0} y1={0} x2={38} y2={6} seed={2304} stroke="#16a34a" />
      <SkLine x1={0} y1={0} x2={-38} y2={6} seed={2305} stroke="#16a34a" />
    </g>
  );
}

/** Frogspawn cluster: many small translucent circles. */
function Frogspawn({ x, y }: { x: number; y: number }): React.ReactElement {
  const positions: Array<[number, number]> = [
    [0, 0], [18, -6], [-16, 8], [8, 14], [-8, -14],
    [26, 8], [-26, -4], [14, -18], [-4, 18], [22, -16],
  ];
  return (
    <g transform={`translate(${x} ${y})`}>
      {positions.map(([px, py], i) => (
        <g key={i} transform={`translate(${px} ${py})`}>
          <SkCircle cx={0} cy={0} r={7} fill="rgba(200,240,255,0.7)" seed={2200 + i} />
          <SkCircle cx={0} cy={0} r={3} fill="#1c1917" seed={2210 + i} />
        </g>
      ))}
    </g>
  );
}

/** Tadpole-with-legs stage: uses skFish body + tiny leg stubs. */
function TadpoleWithLegs({
  x,
  y,
  t: _t,
  legProgress,
}: {
  x: number;
  y: number;
  t: number;
  legProgress: number;
}): React.ReactElement {
  const legLen = legProgress * 16;
  const legAngle = Math.sin(_t * 3) * 18;
  return (
    <g transform={`translate(${x} ${y})`}>
      {skFish(0, 0, _t, 0.9, "#86efac")}
      {/* back legs growing out */}
      <g transform={`translate(22 8) rotate(${legAngle})`}>
        <SkLine x1={0} y1={0} x2={0} y2={legLen} seed={2230} stroke="#4ade80" strokeWidth={3} />
        <SkCircle cx={0} cy={legLen} r={3} fill="#22c55e" seed={2231} />
      </g>
      <g transform={`translate(22 8) rotate(${-legAngle})`}>
        <SkLine x1={0} y1={0} x2={0} y2={legLen} seed={2232} stroke="#4ade80" strokeWidth={3} />
        <SkCircle cx={0} cy={legLen} r={3} fill="#22c55e" seed={2233} />
      </g>
    </g>
  );
}

/** Simple frog from primitives: body + wide head + sticky eyes + four legs. */
function PrimitiveFrog({
  x,
  y,
  t: _t,
  hop,
}: {
  x: number;
  y: number;
  t: number;
  hop: number;
}): React.ReactElement {
  // hop: 0 = sitting, 1 = full leap arc
  const hopY = -Math.sin(hop * Math.PI) * 60;
  const squish = hop < 0.1 ? 1 : 1 - Math.sin(hop * Math.PI) * 0.15; // squish vertically on landing
  const blinkT = Math.sin(_t * 1.8);
  const eyeRy = blinkT < -0.9 ? 1 : 5;

  return (
    <g transform={`translate(${x} ${y + hopY})`}>
      {/* back legs (splayed behind body) */}
      <g transform="translate(-20 10) rotate(150)">
        <SkLine x1={0} y1={0} x2={0} y2={26} seed={2250} stroke="#4ade80" strokeWidth={5} />
        <SkLine x1={0} y1={26} x2={-14} y2={36} seed={2251} stroke="#4ade80" strokeWidth={4} />
        <SkCircle cx={-14} cy={36} r={4} fill="#22c55e" seed={2252} />
      </g>
      <g transform="translate(20 10) rotate(30)">
        <SkLine x1={0} y1={0} x2={0} y2={26} seed={2253} stroke="#4ade80" strokeWidth={5} />
        <SkLine x1={0} y1={26} x2={14} y2={36} seed={2254} stroke="#4ade80" strokeWidth={4} />
        <SkCircle cx={14} cy={36} r={4} fill="#22c55e" seed={2255} />
      </g>
      {/* body */}
      <g transform={`scale(1 ${squish})`}>
        <SkEllipse cx={0} cy={8} rx={30} ry={22} fill="#4ade80" seed={2256} />
        {/* belly */}
        <SkEllipse cx={0} cy={12} rx={18} ry={14} fill="#bbf7d0" seed={2257} />
      </g>
      {/* front legs */}
      <g transform="translate(-28 4)">
        <SkLine x1={0} y1={0} x2={-12} y2={16} seed={2258} stroke="#22c55e" strokeWidth={4} />
        <SkCircle cx={-12} cy={16} r={3} fill="#22c55e" seed={2259} />
      </g>
      <g transform="translate(28 4)">
        <SkLine x1={0} y1={0} x2={12} y2={16} seed={2260} stroke="#22c55e" strokeWidth={4} />
        <SkCircle cx={12} cy={16} r={3} fill="#22c55e" seed={2261} />
      </g>
      {/* head */}
      <SkEllipse cx={0} cy={-14} rx={26} ry={18} fill="#4ade80" seed={2262} />
      {/* mouth */}
      <SkLine x1={-14} y1={-10} x2={14} y2={-10} seed={2263} stroke="#166534" strokeWidth={2} />
      {/* eyes on top of head */}
      <SkCircle cx={-14} cy={-26} r={9} fill="#bbf7d0" seed={2264} />
      <SkCircle cx={14} cy={-26} r={9} fill="#bbf7d0" seed={2265} />
      <SkEllipse cx={-14} cy={-26} rx={5} ry={eyeRy} fill="#1c1917" seed={2266} />
      <SkEllipse cx={14} cy={-26} rx={5} ry={eyeRy} fill="#1c1917" seed={2267} />
      <SkCircle cx={-12} cy={-28} r={1} fill="#ffffff" seed={2268} />
      <SkCircle cx={12} cy={-28} r={1} fill="#ffffff" seed={2269} />
    </g>
  );
}

export const SkinFrogLifeScene: React.FC<SkinSceneProps> = ({
  opacity = 1,
  t = 0,
  label = "From egg to frog!",
}) => {
  const skin = useSkin();
  const LOOP = 9;
  const v = (t % LOOP) / LOOP; // 0..1 per loop

  // stage boundaries
  const S0_START = 0;
  const S0_END = 0.28;   // 0–2.5 s : frogspawn
  const S1_START = 0.22;
  const S1_END = 0.52;   // 2.0–4.7 s : tadpole (skFish)
  const S2_START = 0.46;
  const S2_END = 0.76;   // 4.1–6.8 s : tadpole with legs
  const S3_START = 0.70;
  const S3_END = 1.00;   // 6.3–9.0 s : frog on lily pad hopping

  const oSpawn = stageOpacity(v, S0_START, S0_END, true);
  const oTad = stageOpacity(v, S1_START, S1_END);
  const oLegs = stageOpacity(v, S2_START, S2_END);
  const oFrog = stageOpacity(v, S3_START, S3_END);

  // tadpole swimming around a gentle arc
  const tadProgress = clamp01((v - S1_START) / (S1_END - S1_START));
  const tadX = 280 + Math.sin(tadProgress * Math.PI * 2) * 90;
  const tadY = 250 + Math.cos(tadProgress * Math.PI * 1.5) * 30;

  // tadpole-with-legs: leg growth progress
  const legProgress = clamp01((v - S2_START) / (S2_END - S2_START));
  const legX = 380 + Math.sin(t * 0.9) * 40;
  const legY = 250 + Math.cos(t * 1.1) * 20;

  // frog hop: half a cosine per 2 seconds, loops naturally
  const hopPhase = (t * 0.5) % 1;
  const hop = Math.abs(Math.sin(hopPhase * Math.PI));

  const ground = 310;

  // pond
  const pondY = 230;

  // stage label
  const stageName =
    oSpawn > 0.2
      ? "Stage 1: Frogspawn"
      : oTad > 0.2
      ? "Stage 2: Tadpole!"
      : oLegs > 0.2
      ? "Stage 3: Growing legs"
      : "Stage 4: Frog!";
  const stageOp = Math.max(oSpawn, oTad, oLegs, oFrog);

  return (
    <g opacity={opacity}>
      {/* sky */}
      <rect width={800} height={450} fill={skin.surface} />

      {/* sky tint */}
      <SkRect x={0} y={0} w={800} h={ground} fill="#e0f2fe" seed={2400} opacity={0.4} />

      {/* ground */}
      <SkRect x={0} y={ground} w={800} h={450 - ground} fill="#bbf7d0" seed={2401} />

      {/* pond area */}
      <SkEllipse cx={360} cy={pondY} rx={240} ry={90} fill="#7dd3fc" seed={2402} />
      <SkEllipse cx={360} cy={pondY} rx={230} ry={82} fill="#bae6fd" seed={2403} opacity={0.6} />

      {/* water ripples (animate with t) */}
      {[0, 1, 2].map((i) => {
        const rPhase = (t * 0.5 + i * 0.33) % 1;
        const rr = 30 + rPhase * 80;
        return (
          <SkEllipse
            key={i}
            cx={360}
            cy={pondY + 10}
            rx={rr}
            ry={rr * 0.35}
            fill="none"
            stroke="#93c5fd"
            strokeWidth={1}
            seed={2404 + i}
            opacity={1 - rPhase}
          />
        );
      })}

      {/* lily pads */}
      <LilyPad x={500} y={240} scale={1.2} />
      <LilyPad x={220} y={250} scale={0.8} />

      {/* ── Stage 0: Frogspawn ─────────────────────────────────────── */}
      <g opacity={oSpawn}>
        <Frogspawn x={320} y={pondY + 10} />
      </g>

      {/* ── Stage 1: Tadpole swimming ──────────────────────────────── */}
      <g opacity={oTad}>
        {skFish(tadX, tadY, t, 0.75, "#6ee7b7")}
      </g>

      {/* ── Stage 2: Tadpole with legs ─────────────────────────────── */}
      <g opacity={oLegs}>
        <TadpoleWithLegs
          x={legX}
          y={legY}
          t={t}
          legProgress={legProgress}
        />
      </g>

      {/* ── Stage 3: Frog on lily pad hopping ─────────────────────── */}
      <g opacity={oFrog}>
        <PrimitiveFrog x={500} y={216} t={t} hop={hop} />
      </g>

      {/* main caption */}
      <g transform="translate(400 38)">
        <rect x={-200} y={-22} width={400} height={40} rx={20} fill="rgba(255,255,255,0.88)" stroke="#cbd5e1" strokeWidth={1.5} />
        <text
          x={0}
          y={4}
          textAnchor="middle"
          fill={skin.textInk}
          fontSize={16}
          fontWeight={700}
          fontFamily={skin.fontFamily}
        >
          {label}
        </text>
      </g>

      {/* stage label */}
      <StageLabel
        text={stageName}
        opacity={stageOp}
        textInk={skin.textInk}
        fontFamily={skin.fontFamily}
      />
    </g>
  );
};
